import React from 'react';
import {ProductList} from '../ProductList/ProductList';
import {Filters} from '../Filters/Filters';
import {IGallerySantaProps, IPropsInjectedByViewerScript} from '../../../types/galleryTypes';
import {Sort} from '../Sort/Sort';
import s from './GalleryApp.scss';
import classNames from 'classnames';

import {Omit} from '@wix/native-components-infra/dist/src/types/types';
import autobind from 'autobind-decorator';
import _ from 'lodash';
import {Announcer} from '@wix/wixstores-client-core/dist/es/src/a11y/announcer';
import {
  IProvidedTranslationProps,
  withTranslations,
} from '@wix/wixstores-client-common-components/dist/es/src/outOfIframes/translations';
import {MobileSort} from '../MobileSort/MobileSort';
import {withGlobals} from '../../../globalPropsContext';
import {IGalleryGlobalProps} from '../../galleryGlobalStrategy';
import {MobileFilters} from '../MobileFilters/MobileFilters';

const inlineCss = require('!raw-loader!../../../styles/inlineStyle.css');

export enum DataHook {
  Root = 'gallery-app-root',
  Content = 'gallery-app-content',
  Container = 'gallery-app-container',
  SideFilters = 'gallery-aside-filters',
}

export type IGalleryAppProps = Omit<IPropsInjectedByViewerScript & IGallerySantaProps, IGalleryGlobalProps['globals']> &
  IGalleryGlobalProps &
  IProvidedTranslationProps;

@withGlobals
@withTranslations()
@autobind
export class GalleryApp extends React.Component<IGalleryAppProps> {
  private a11yAnnouncer: Announcer;

  constructor(props) {
    super(props);

    this.state = {
      isSSR: props.isSSR,
    };
  }

  public componentDidMount() {
    this.a11yAnnouncer = new Announcer('gallery-announcer');
    this.props.host.registerToComponentDidLayout(this.reportAppLoaded);
  }

  public componentDidUpdate(prevProps: IGalleryGlobalProps) {
    this.announceIfFilterResultChanged(prevProps);
  }

  private announceIfFilterResultChanged(prevProps) {
    const haveFiltersChanged = !_.isEqual(this.props.globals.filterModels, prevProps.globals.filterModels);

    if (haveFiltersChanged && this.props.globals.products) {
      this.a11yAnnouncer.announce(
        this.props.t('sr.ANNOUNCE_FOUND_ITEMS_ON_FILTERS_UPDATE', {
          numberOfFoundProducts: this.props.globals.products && this.props.globals.products.length,
        })
      );
    }
  }

  public componentWillUnmount() {
    this.a11yAnnouncer.cleanup();
  }

  private reportAppLoaded() {
    /* istanbul ignore next: hard to test it */
    if (this.props.globals.isInteractive && typeof this.props.onAppLoaded === 'function') {
      this.props.onAppLoaded();
    }
  }

  private shouldShowGalleryApp() {
    return this.props.isLoaded && !this.props.globals.showShowLightEmptyState;
  }

  private shouldShowFilters() {
    return this.props.globals.filterModels.length > 0;
  }

  private shouldShowSort() {
    return this.props.globals.shouldShowSort;
  }

  private renderFilters() {
    const {clearFilters, shouldShowClearFilters} = this.props.globals;
    return <Filters shouldShowClearFiltersButton={shouldShowClearFilters} clearFilters={clearFilters} />;
  }

  private renderMobile() {
    return (
      <div className={s.mobileFiltersAndSort}>
        {this.shouldShowFilters() && <MobileFilters />}
        {this.shouldShowSort() && <MobileSort areFiltersEnabled={this.shouldShowFilters()} />}
      </div>
    );
  }

  private renderSideFilters(classnames) {
    return (
      <aside className={classnames} data-hook={DataHook.SideFilters}>
        {this.renderFilters()}
      </aside>
    );
  }

  private renderDesktopSort() {
    return (
      <div className={s.sortRow}>
        <Sort />
      </div>
    );
  }

  private renderProducts() {
    const {hasSelectedFilters} = this.props.globals;

    return <ProductList hasFilters={hasSelectedFilters} />;
  }

  public render() {
    const {full_width: isFullWidth, responsive} = this.props.globals.styleParams.booleans;
    const {isMobile} = this.props.globals;
    const isDesktop = !isMobile;

    if (!this.shouldShowGalleryApp()) {
      return null;
    }

    const shouldRenderSideFilters = isDesktop && this.shouldShowFilters();
    const shouldRenderDesktopSort = isDesktop && this.shouldShowSort();

    const classnames = {
      app: classNames({
        [s.galleryApp]: true,
        deviceMobile: isMobile,
      }),
      content: classNames({
        [s.content]: true,
        [s.contentResponsive]: responsive,
        [s.fullWidth]: isFullWidth,
      }),
      container: classNames({
        [s.container]: true,
        [s.filtersAndProductList]: isDesktop && this.shouldShowFilters(),
      }),
      filters: classNames({
        [s.filters]: true,
        [s.withSort]: shouldRenderDesktopSort,
      }),
    };

    return (
      <>
        <style dangerouslySetInnerHTML={{__html: inlineCss}} />
        <div data-hook={DataHook.Root} data-is-responsive={responsive} className={classnames.app}>
          <div data-hook={DataHook.Content} className={classnames.content}>
            {shouldRenderSideFilters && this.renderSideFilters(classnames.filters)}
            <div data-hook={DataHook.Container} className={classnames.container}>
              {shouldRenderDesktopSort && this.renderDesktopSort()}
              {isMobile && this.renderMobile()}
              {this.renderProducts()}
            </div>
          </div>
        </div>
      </>
    );
  }
}
